import { formatMediaPlaybackDuration } from "common247";

/**
 * Formats byte values into human-readable strings (e.g. 450 KB, 1.8 MB).
 * @param {number | null} [bytes] - Byte count.
 * @returns {string} Human-readable file size.
 */
export function formatFileSize(bytes?: number | null): string {
  if (bytes === null || bytes === undefined || isNaN(bytes) || bytes <= 0) return "Unknown size";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

/**
 * Formats a MIME content-type into a user-friendly format badge label (e.g. "JPEG", "PNG", "MP4").
 * @param {string} [contentType] - MIME content type.
 * @returns {string} Short format label.
 */
export function formatContentType(contentType?: string): string {
  if (!contentType) return "FILE";
  const lower = contentType.toLowerCase();
  if (lower === "application/pdf" || lower.endsWith(".pdf")) return "PDF";
  if (
    lower.includes("wordprocessingml") ||
    lower.includes("msword") ||
    lower.endsWith(".docx") ||
    lower.endsWith(".doc")
  ) {
    return "DOCX";
  }
  if (lower.includes("spreadsheetml") || lower.includes("excel") || lower.endsWith(".xlsx")) return "XLSX";
  if (lower.includes("presentationml") || lower.includes("powerpoint") || lower.endsWith(".pptx")) return "PPTX";
  if (lower === "text/plain" || lower.endsWith(".txt")) return "TXT";
  if (lower === "text/csv" || lower.endsWith(".csv")) return "CSV";
  const parts = contentType.split("/");
  const sub = parts[1] || parts[0];
  const cleaned = sub.replace("x-", "").replace("+xml", "").toUpperCase();
  if (cleaned === "QUICKTIME") return "MOV";
  if (cleaned === "MPEG" || cleaned === "MP4") return "MP4";
  return cleaned;
}

/**
 * Formats duration in milliseconds into mm:ss format.
 * Delegates to the shared common247 `formatMediaPlaybackDuration` helper.
 * @param {number} [durationMs] - Duration in milliseconds.
 * @returns {string} Formatted duration string.
 */
export function formatDuration(durationMs?: number): string {
  return formatMediaPlaybackDuration(durationMs);
}

/**
 * Formats dimensions into "WIDTH × HEIGHT" format.
 * @param {number} [width] - Width in pixels.
 * @param {number} [height] - Height in pixels.
 * @returns {string} Dimensions representation.
 */
export function formatDimensions(width?: number, height?: number): string {
  if (!width || !height) return "";
  return `${width} × ${height}`;
}

/**
 * Formats camera hardware and exposure settings from extra metadata.
 * @param {Record<string, any>} [extra] - Extra metadata key-value pairs.
 * @returns {string | null} Human-readable camera and exposure summary or null.
 */
export function formatCameraSettings(extra?: Record<string, any>): string | null {
  if (!extra) return null;
  const parts: string[] = [];
  const make = extra.make ? String(extra.make).trim() : "";
  const model = extra.model ? String(extra.model).trim() : "";
  if (make || model) {
    if (model && make && model.toLowerCase().startsWith(make.toLowerCase())) {
      parts.push(model);
    } else {
      parts.push([make, model].filter(Boolean).join(" "));
    }
  }

  const expParts: string[] = [];
  if (extra.fNumber !== null && extra.fNumber !== undefined) {
    expParts.push(`ƒ/${extra.fNumber}`);
  }
  if (extra.exposureTime !== null && extra.exposureTime !== undefined) {
    const rawExp = extra.exposureTime;
    const numExp = typeof rawExp === "number" ? rawExp : parseFloat(String(rawExp));
    if (!isNaN(numExp) && numExp < 1 && numExp > 0) {
      expParts.push(`1/${Math.round(1 / numExp)}s`);
    } else {
      expParts.push(`${rawExp}s`);
    }
  }
  if (extra.iso !== null && extra.iso !== undefined) {
    expParts.push(`ISO ${extra.iso}`);
  }
  if (extra.focalLength !== null && extra.focalLength !== undefined) {
    expParts.push(`${extra.focalLength}mm`);
  }

  if (expParts.length > 0) {
    parts.push(expParts.join(" • "));
  }

  return parts.length > 0 ? parts.join(" — ") : null;
}

/**
 * Truncates a SHA-256 hash to a readable short checksum tag.
 * @param {string} [hash] - SHA-256 hash.
 * @returns {string} Truncated hash (e.g. "SHA: a1b2c3d4...").
 */
export function formatShortHash(hash?: string): string {
  if (!hash) return "";
  const clean = hash.trim();
  if (clean.length <= 14) return `SHA: ${clean}`;
  return `SHA: ${clean.substring(0, 8)}...${clean.substring(clean.length - 4)}`;
}
